import { useEffect, useRef, useState } from "react"
import { useSession } from "../auth/session"
import { changePassword } from "../api/cognitoMfa"
import Spinner from "./widgets/Spinner"
import { backdropDismiss } from "../utils/backdropDismiss"

// Maps Cognito exception names (set by cognitoCall from __type) to friendly
// messages; InvalidPasswordException falls through to Cognito's own message
// because it spells out the pool's password policy.
function passwordErrorMessage(err: unknown): string {
  if (err instanceof Error) {
    if (err.name === "NotAuthorizedException") return "Current password is incorrect."
    if (err.name === "LimitExceededException") return "Too many attempts — please wait a while and try again."
    return err.message
  }
  return "unknown error"
}

// In-app password change via browser→Cognito ChangePassword. Deliberately no
// GlobalSignOut afterwards: unlike MFA enrollment there is no invariant that
// stale sessions would bypass, and it would also revoke this session's
// refresh token.
export default function ChangePasswordDialog({ onClose }: { onClose: () => void }) {
  const session = useSession()
  const accessToken = session.accessToken ?? ""
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [step, setStep] = useState<"form" | "busy" | "error" | "success">("form")
  const [errorMessage, setErrorMessage] = useState("")
  const [currentPassword, setCurrentPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  const confirmMismatch = confirmPassword.length > 0 && newPassword !== confirmPassword
  const formValid = currentPassword.length > 0 && newPassword.length > 0 && newPassword === confirmPassword

  const handleSubmit = async () => {
    setStep("busy")
    setErrorMessage("")
    try {
      await changePassword(accessToken, currentPassword, newPassword)
      setStep("success")
    } catch (err) {
      setErrorMessage(passwordErrorMessage(err))
      setStep("error")
    }
  }

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog"
      onClose={onClose}
      {...backdropDismiss(() => { if (step !== "busy") dialogRef.current?.close() })}
    >
      <div className="redeem-dialog-content">
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()} aria-label="Close">&times;</button>
        <h2 className="redeem-dialog-title">Change Password</h2>

        {step !== "success" && (
          <>
            <div className="generate-codes-form">
              <label className="generate-codes-field">
                <span className="generate-codes-label">Current password</span>
                <input
                  type="password"
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="generate-codes-input"
                  disabled={step === "busy"}
                  autoFocus
                />
              </label>
              <label className="generate-codes-field">
                <span className="generate-codes-label">New password</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="generate-codes-input"
                  disabled={step === "busy"}
                />
              </label>
              <label className="generate-codes-field">
                <span className="generate-codes-label">Confirm new password</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="generate-codes-input"
                  disabled={step === "busy"}
                />
              </label>
              {confirmMismatch && (
                <p className="generate-codes-error">Passwords don't match.</p>
              )}
            </div>

            {step === "error" && errorMessage && (
              <p className="generate-codes-error">{errorMessage}</p>
            )}

            <div className="redeem-actions">
              <button onClick={() => dialogRef.current?.close()} disabled={step === "busy"}>Cancel</button>
              <button
                className="btn-upgrade"
                disabled={step === "busy" || !formValid}
                onClick={handleSubmit}
              >
                {step === "busy" ? <><Spinner size={12} /> Changing…</> : "Change password"}
              </button>
            </div>
          </>
        )}

        {step === "success" && (
          <>
            <p className="generate-codes-success">Password changed.</p>
            <p>Other devices stay signed in until their sessions expire.</p>
            <div className="redeem-actions">
              <button className="btn-upgrade" onClick={() => dialogRef.current?.close()}>Done</button>
            </div>
          </>
        )}
      </div>
    </dialog>
  )
}
